/**
 * useSavedContent Hook
 *
 * Feature-specific hook for the Saved Content (Watch later) page.
 *
 * Architecture: Page → Hook → UseCase → Service
 *
 * Mirrors Flutter's SavedContentScreenState
 * (lib/presentation/screens/content/saved_content_screen.dart).
 *
 * Responsibilities:
 * - Snapshot the locally-stored SavedContent records once on init via
 *   `savedContentService.listLocalSavedContent(currentUser)` and paginate
 *   against that snapshot, matching Flutter's `_getData`
 *   (saved_content_screen.dart:115-157).
 * - Listen reactively to secure-storage writes under the SAVED_CONTENT
 *   prefix (`SavedContent_`) via `useListenToSecureStorageUpdatesByPrefix`
 *   with `loadInitialItems: false`. The hook is the canonical
 *   live-update channel; `loadInitialItems` is `false` because `init()`
 *   owns the initial load through the domain service. This matches the
 *   pattern used by `useLikedVideos` and `useUnlockedVideos`.
 * - SavedContentStorage has no `liked` boolean, so an unsave is a
 *   `remove(...)` (see `saveContentToWatchLaterUseCase.ts`). The hook
 *   surfaces those as entries in `deletedKeys`; the live-update effect
 *   below partitions live state into removes (via `deletedKeys`) and
 *   adds (via `liveLocalItems`).
 * - Surface loading / loading-more / hasMore / error to the UI.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useListenToSecureStorageUpdatesByPrefix } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { SavedContentStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { RootState } from '@/presentation/store';
import { savedContentService } from '@/domain/services/savedContent/savedContentService';
import { fetchSavedContentPageUseCase } from '@/domain/usecases/savedContent/fetchSavedContentPageUseCase';
import { getUserMediaByPathUseCase } from '@/domain/usecases/userMedia/getUserMediaByPathUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { useLocale } from '@/presentation/contexts/LocaleContext';

export interface UseSavedContentResult {
  medias: UserMediaEntity[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  init: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export const useSavedContent = (): UseSavedContentResult => {
  const { l10n } = useLocale();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Mirror `currentUser` into a ref so the live-update effect doesn't re-run
  // every time the auth listener mirrors SecureStorage into Redux (which
  // produces a fresh `UserEntity` reference even when nothing relevant
  // changed — see .claude/context/state-slices.md). The use cases inside
  // the live-update IIFE need the full object, so ref-mirroring is cleaner
  // than pulling out scalars.
  const currentUserRef = useRef(currentUser);
  useEffect(() => {
    currentUserRef.current = currentUser;
  });

  // Pure live-update channel — `loadInitialItems: false` because `init()`
  // below loads the existing items explicitly through the service. The
  // hook's `items` starts empty and only grows when a new save is written
  // while the page is open. Deletes (SavedContentStorage has no `liked`
  // flag, so unsaves are `remove(...)` writes) arrive on `deletedKeys`.
  const { items: liveLocalItems, deletedKeys: liveDeletedKeys } =
    useListenToSecureStorageUpdatesByPrefix<SavedContentStorage>(
      SECURE_STORAGE_KEYS.SAVED_CONTENT,
      (item) => item.path,
      false,
    );

  const [medias, setMedias] = useState<UserMediaEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The snapshot is taken once on init and is the basis for every page
  // fetch — matches Flutter's `_savedContentItems`, which is read once
  // (synchronously, in `initState`) and re-sliced by `_getData`.
  const localItemsSnapshotRef = useRef<SavedContentStorage[]>([]);
  const pageIndexRef = useRef(0);
  // Every path already covered by the pagination snapshot or by a
  // subsequent live-add. Used to:
  //  - prevent a duplicate prepend when an `setEncrypted` event lands for
  //    a path that was already in the snapshot (e.g. a race between
  //    `listLocalSavedContent` and a save toggle).
  //  - allow a re-save after an unsave to be treated as a new add (the
  //    delete branch below removes the path from this set).
  //  - dedupe the append-only `deletedKeys` stream surfaced by the live
  //    hook (a path only triggers a remove on the first observation).
  const seenPathsRef = useRef<Set<string>>(new Set());
  // Guard so the live-update effect doesn't act before init has resolved
  // (otherwise initial paths would be treated as "new" and double-fetched).
  const hasInitializedRef = useRef(false);
  const isLoadingMoreRef = useRef(false);

  const init = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const snapshot = await savedContentService.listLocalSavedContent(currentUser);
      localItemsSnapshotRef.current = snapshot;
      pageIndexRef.current = 0;

      const result = await fetchSavedContentPageUseCase.execute({
        localItems: snapshot,
        pageIndex: 0,
        currentUser,
      });

      // Seed with EVERY path in the snapshot — see the ref's declaration
      // for the rationale.
      const initialSeen = new Set<string>();
      for (const item of snapshot) {
        initialSeen.add(item.path);
      }
      seenPathsRef.current = initialSeen;

      setMedias(result.medias);
      setHasMore(result.hasMore);
    } catch (err) {
      appLogger.error('Error fetching saved content', 'useSavedContent.init', { error: err as Error });
      setError(l10n.savedContent.errorLoading);
      setMedias([]);
      setHasMore(false);
    } finally {
      // Set the ref before flipping isLoading: the live-update effect
      // gates on !isLoading and reads this ref, so the ref must be true
      // by the time the re-render from setIsLoading(false) fires it.
      hasInitializedRef.current = true;
      setIsLoading(false);
    }
  }, [currentUser, l10n]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (isLoadingMoreRef.current || !hasMore) {
      return;
    }

    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;
    setError(null);

    try {
      const nextIndex = pageIndexRef.current + 1;
      const result = await fetchSavedContentPageUseCase.execute({
        localItems: localItemsSnapshotRef.current,
        pageIndex: nextIndex,
        currentUser,
      });

      pageIndexRef.current = nextIndex;
      setMedias((prev) => [...prev, ...result.medias]);
      setHasMore(result.hasMore);
    } catch (err) {
      appLogger.error('Error loading more saved content', 'useSavedContent.loadMore', { error: err as Error });
      setError(l10n.savedContent.loadMoreFailed);
    } finally {
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [hasMore, currentUser, l10n]);

  // Reactive add/remove for save toggles — mirrors Flutter
  // `_onBoxDataEvent` (saved_content_screen.dart:93-113). Routed through
  // `useListenToSecureStorageUpdatesByPrefix` (see top-of-file rationale):
  //
  //  - `liveDeletedKeys` is the append-only stream of `remove(...)`
  //    events under the SAVED_CONTENT prefix (SavedContentStorage has no
  //    `liked` flag, so this is the only delete signal). We dedupe via
  //    `seenPathsRef`: a path only triggers a remove on the first
  //    observation, and is then dropped from the set so a subsequent
  //    re-save is treated as a new add.
  //  - `liveLocalItems` contains every saved-content write the hook has
  //    seen since mount. We dedupe via `seenPathsRef` to avoid double-
  //    fetching paths already in the initial snapshot, and apply the
  //    `hiddenContent` snapshot-filter parity (saved_content_screen.dart:83)
  //    on each add.
  //
  // Structural divergence vs `useLikedVideos`: that hook can partition
  // a single `liveLocalItems` array into removes + adds because
  // `UserMediaLikeStorage.liked: false` keeps the unliked record in
  // `items`. Our delete path is a `remove(...)` (no `liked` flag), so
  // the hook drops the entry from `items` and surfaces it via
  // `deletedKeys` instead — see the JSDoc on
  // `useListenToSecureStorageUpdatesByPrefix`.
  //
  // `isLoading` is a dep (not just the ref) so the effect re-runs after
  // init finishes — covers the race where a live write lands between
  // `listLocalSavedContent` resolving and `setIsLoading(false)`.
  useEffect(() => {
    if (!hasInitializedRef.current || isLoading) {
      return;
    }

    // Apply removes synchronously — no Firestore lookup needed.
    if (liveDeletedKeys.length > 0) {
      const removedPaths = new Set<string>();
      for (const path of liveDeletedKeys) {
        if (seenPathsRef.current.has(path)) {
          removedPaths.add(path);
          seenPathsRef.current.delete(path);
        }
      }
      if (removedPaths.size > 0) {
        setMedias((prev) => prev.filter((media) => !removedPaths.has(media.relativePath)));
      }
    }

    // Partition adds: anything in `liveLocalItems` whose path is not
    // yet in `seenPathsRef` is a new save we need to resolve.
    const adds: SavedContentStorage[] = [];
    for (const item of liveLocalItems) {
      if (!seenPathsRef.current.has(item.path)) {
        adds.push(item);
      }
    }

    if (adds.length === 0) {
      return;
    }

    let cancelled = false;
    const hiddenContent = currentUserRef.current?.hiddenContent ?? [];

    (async () => {
      for (const item of adds) {
        // Snapshot filter parity (saved_content_screen.dart:83): drop
        // items whose media id is in `hiddenContent`. Still mark as
        // seen so we don't re-attempt on every subsequent storage event
        // for the same path.
        if (hiddenContent.includes(item.id)) {
          seenPathsRef.current.add(item.path);
          continue;
        }

        try {
          const media = await getUserMediaByPathUseCase.execute(item.path, currentUserRef.current);
          if (cancelled) {
            return;
          }
          // Mark as seen even when the use case returned null (filtered
          // out by the blocked/hidden guard inside the use case) — avoids
          // re-fetching on every subsequent storage event for this path.
          seenPathsRef.current.add(item.path);
          if (media) {
            setMedias((prev) => [media, ...prev]);
          }
        } catch (err) {
          appLogger.error(
            'Error resolving newly-saved media',
            'useSavedContent.onLiveAdd',
            { error: err as Error, context: { path: item.path } },
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [liveLocalItems, liveDeletedKeys, isLoading]);

  return {
    medias,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    init,
    loadMore,
  };
};
